import React, { useRef, useState } from 'react';
import { Modal, Select, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Table } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import usePaginationHook from '../../../../../utils/usePagination';
import { FaDownload, FaUpload } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { AlertConfirm, AlertSuccess } from '../../../../util/Alert';
import fitMissionServices from '../../../../../services/fit-mission.services';
import dayjs from 'dayjs';

const pageSize = 10;

const LegacyMissionMemberListModal = ({ open, handleClose, mission }) => {
  const queryClient = useQueryClient();
  const {
    data: missionMemberLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();

  const { control, watch } = useForm({
    defaultValues: {
      searchText: '',
      memberType: '',
      isPartner: undefined,
    },
  });

  fitMissionServices.useQueryGetMissionMemberLists(
    {
      take: pageSize,
      page,
      missionId: mission.id,
      searchText: watch('searchText'),
      memberType: watch('memberType'),
      // isPartner: watch('isPartner'), // --- IGNORE --- ถ้าเงื่อนไข isPartner เปิดใช้งานนี้กลับมา
      isPartner: undefined,
    },
    (data) => {
      setData(data.data);
      setTotal(data.meta.total);
    },
  );

  const { mutate: uploadMissionMemberListExcel } =
    fitMissionServices.useMutationUploadMissionMemberListExcel((data) => {
      setTimeout(() => {
        inputRef.current.value = null;

        queryClient.invalidateQueries({
          queryKey: ['get-mission-member-list'],
          refetchType: 'all',
        });

        setLoading(false);
        AlertSuccess('อัพโหลดสำเร็จ');
      }, 600);
    });

  const { mutate: deleteMissionMemberList } =
    fitMissionServices.useMutationDeleteMissionMemberList(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-mission-member-list'],
        refetchType: 'all',
      });
    });

  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);

  const _HandleUploadMissionMemberListExcel = (data) => {
    setLoading(true);
    uploadMissionMemberListExcel({
      data,
      missionId: mission.id,
    });
  };

  const { mutate: downloadMissionMemberListExcel } =
    fitMissionServices.useMutationDownloadMissionMemberListExcel(() => {
      setTimeout(() => {
        setLoadingDownload(false);
      }, 600);
    });

  const columns = [
    {
      title: 'NO',
      dataIndex: 'index',
      key: 'index',
      render: (_, record, index) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'รหัสนักธุรกิจ',
      dataIndex: 'memberCode',
      key: 'memberCode',
      render: (_, record) => <span>{record.memberCode}</span>,
      width: 150,
    },
    {
      title: 'ชื่อ-สกุล',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (_, record) => {
        const user = record.user;
        const partner = record.partner;
        if (!user) return '-';
        if (partner) {
          return (
            <div style={{ lineHeight: 1.4 }}>
              <div>
                {user.FirstNameTh} {user.SurNameTh}
              </div>
              <div style={{ color: '#888', fontSize: '0.9em' }}>
                {partner.FirstNameTh} {partner.SurNameTh}
              </div>
            </div>
          );
        }
        return (
          <span>
            {user.FirstNameTh} {user.SurNameTh}
          </span>
        );
      },
    },
    {
      title: 'HonorCode',
      dataIndex: 'honorCode',
      key: 'honorCode',
      render: (_, record) => <span>{record?.user?.HonorCode || '-'}</span>,
      width: 120,
    },
    {
      title: 'TeamName',
      dataIndex: 'teamName',
      key: 'teamName',
      render: (_, record) => <span>{record?.user?.TeamName || '-'}</span>,
      width: 150,
    },
    {
      title: 'ประเภท',
      dataIndex: 'memberType',
      key: 'memberType',
      render: (_, record) => (
        <span>
          {record.memberType === 'Distributor' && 'นักธุรกิจ'}
          {record.memberType === 'Member' && 'VIP Member'}
        </span>
      ),
      width: 120,
    },
    // { // --- IGNORE --- ถ้าเงื่อนไข isPartner เปิดใช้งานนี้กลับมา
    //   title: 'สถานะ',
    //   dataIndex: 'isPartner',
    //   key: 'isPartner',
    //   render: (_, record) => (
    //     <span>{record.isPartner ? 'รหัสร่วม' : 'รหัสหลัก'}</span>
    //   ),
    //   width: 100,
    // },
    {
      title: 'อัปเดตเมื่อ',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, record) => (
        <span>
          {record?.updatedAt
            ? dayjs(record.updatedAt).format('DD MMM YY เวลา HH:mm น.')
            : '-'}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ display: 'flex', justifyContent: 'center' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={async () => {
              const confirm = await AlertConfirm('ลบข้อมูล');
              if (confirm) deleteMissionMemberList({ id: record.id });
            }}
          >
            <MdDelete />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={'รหัสทั้งหมด'}
      visible={open}
      cancelText="ปิด"
      onCancel={handleClose}
      width={'80vw'}
      okButtonProps={{ style: { display: 'none' } }}
      zIndex={1010}
    >
      <div
        style={{ display: 'flex', alignItems: 'center' }}
        className="mb-2 mt-2"
      >
        <div style={{ flex: 1 }} />
        <input
          type="file"
          id={'mission-member-list-upload'}
          onChange={(e) => {
            if (e.target.files?.length > 0) {
              const formData = new FormData();
              formData.append('file', e.target.files[0]);
              _HandleUploadMissionMemberListExcel(formData);
            }
          }}
          style={{ display: 'none' }}
          ref={inputRef}
        />
        <Button
          className="mr-2"
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.click();
            }
          }}
          loading={loading}
          type="primary"
        >
          <FaUpload className="mr-2" />
          อัพโหลด excel
        </Button>
        <Button
          ghost
          onClick={() => {
            setLoadingDownload(true);
            downloadMissionMemberListExcel({
              take: -1,
              page: 1,
              missionId: mission.id,
              memberCode: watch('searchText'),
              memberType: watch('memberType'),
              isPartner: watch('isPartner'),
            });
          }}
          loading={loadingDownload}
          type="primary"
        >
          <FaDownload className="mr-2" />
          โหลด excel
        </Button>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end' }} className="mb-2">
        <Controller
          name="searchText"
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <Input
                value={value}
                onChange={onChange}
                placeholder="ค้นหาด้วยรหัสนักธุรกิจ"
                className="mr-2"
                style={{ width: 300 }}
              />
            );
          }}
        />
        <Controller
          name="memberType"
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography.Text>ประเภทสมาชิก</Typography.Text>
                <Select
                  value={value}
                  style={{ width: 200 }}
                  onChange={onChange}
                  placeholder="ประเภทสมาชิก"
                  className="mr-2"
                  allowClear
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                >
                  {[
                    { label: 'ทั้งหมด', value: '' },
                    { label: 'นักธุรกิจ', value: 'Distributor' },
                    { label: 'VIP Member', value: 'Member' },
                  ].map((item) => {
                    return (
                      <Select.Option key={item.value} value={item.value}>
                        {item.label}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
            );
          }}
        />
        {/* <Controller // --- IGNORE --- ถ้าเงื่อนไข isPartner เปิดใช้งานนี้กลับมา
          name="isPartner"
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <Select
                value={value}
                style={{ width: 200 }}
                onChange={onChange}
                placeholder="สถานะรหัส"
                allowClear
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              >
                {[
                  { label: 'ทั้งหมด', value: undefined },
                  { label: 'รหัสหลัก', value: false },
                  { label: 'รหัสร่วม', value: true },
                ].map((item) => {
                  return (
                    <Select.Option key={String(item.value)} value={item.value}>
                      {item.label}
                    </Select.Option>
                  );
                })}
              </Select>
            );
          }}
        /> */}
        <div
          style={{
            backgroundColor: '#cfe2f3',
            borderRadius: 5,
            padding: 10,
            marginLeft: 16,
            border: '1px solid #6fa8dc',
            width: 200,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography.Text>ทั้งหมด: {total}</Typography.Text>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={missionMemberLists}
        style={{ backgroundColor: '#fff' }}
        rowKey="id"
        onChange={({ current }) => {
          setPage(current);
        }}
        pagination={{
          pageSize,
          total,
        }}
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  );
};

export default LegacyMissionMemberListModal;
